import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Heart, Lock, ChevronDown } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

const Footer = () => {
  const isMobile = useIsMobile();
  const [showAllLinks, setShowAllLinks] = useState(false);

  if (isMobile) {
    return (
      <footer className="bg-primary text-primary-foreground">
        <div className="container mx-auto px-6 py-12">
          {/* Brand Section */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center space-x-3 mb-4">
              <img 
                src="/lovable-uploads/bd8dbf9f-c206-448e-a1a3-20eeb1e8868d.png" 
                alt="Safurra" 
                className="h-8 w-8 brightness-0 invert object-contain"
              />
              <div>
                <h3 className="text-lg font-bold">Safurra</h3>
              </div>
            </div>
          </div>

          {/* Essential Links */}
          <div className="text-center space-y-3 mb-6">
            <Link to="/legal/privacy" className="block text-sm text-primary-foreground/80 hover:text-primary-foreground">
              Privacy Policy
            </Link>
            <Link to="/legal/terms" className="block text-sm text-primary-foreground/80 hover:text-primary-foreground">
              Terms of Service
            </Link>
            <Link to="/contact" className="block text-sm text-primary-foreground/80 hover:text-primary-foreground">
              Contact Us
            </Link>
          </div>

          {/* More Links Dropdown */}
          <div className="text-center mb-6">
            <button
              onClick={() => setShowAllLinks(!showAllLinks)}
              className="flex items-center justify-center space-x-1 text-sm text-primary-foreground/80 mx-auto"
            >
              <span>More Links</span>
              <ChevronDown className={`h-4 w-4 transition-transform ${showAllLinks ? 'rotate-180' : ''}`} />
            </button>
            
            {showAllLinks && (
              <div className="mt-4 space-y-2 border-t border-primary-foreground/20 pt-4">
                <Link to="/about" className="block text-sm text-primary-foreground/80">About Us</Link>
                <Link to="/how-it-works" className="block text-sm text-primary-foreground/80">How It Works</Link>
                <Link to="/carriers" className="block text-sm text-primary-foreground/80">Carriers</Link>
                <Link to="/legal/licenses" className="block text-sm text-primary-foreground/80">Licenses</Link>
                <Link to="/legal/disclosures" className="block text-sm text-primary-foreground/80">Disclosures</Link>
                <Link to="/legal/accessibility" className="block text-sm text-primary-foreground/80">Accessibility</Link>
              </div>
            )}
          </div>

          {/* Copyright */}
          <div className="text-center border-t border-primary-foreground/20 pt-6">
            <p className="text-xs text-primary-foreground/80">
              © 2024 Safurra. Licensed Insurance Agency
            </p>
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className="bg-primary text-primary-foreground">
      <div className="container mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand Section */}
          <div className="md:col-span-2">
            <div className="flex items-center space-x-3 mb-6">
            <img 
              src="/lovable-uploads/bd8dbf9f-c206-448e-a1a3-20eeb1e8868d.png" 
              alt="Safurra" 
              className="h-10 w-10 brightness-0 invert object-contain"
            />
              <div>
                <h3 className="text-xl font-bold">Safurra</h3>
                <p className="text-primary-foreground/80 text-sm">Protection, without pressure</p>
              </div>
            </div>
            <p className="text-primary-foreground/80 text-sm leading-relaxed mb-6 max-w-md">
              We make life insurance simple, transparent, and accessible. Get personalized quotes 
              from top-rated carriers without the hassle of agent calls or pressure tactics.
            </p>
            <div className="flex items-center space-x-6 text-xs text-primary-foreground/80">
              <div className="flex items-center space-x-1">
                <Shield className="h-4 w-4" />
                <span>Licensed & Secure</span>
              </div>
              <div className="flex items-center space-x-1">
                <Lock className="h-4 w-4" />
                <span>Privacy Protected</span>
              </div>
              <div className="flex items-center space-x-1">
                <Heart className="h-4 w-4" />
                <span>Customer First</span>
              </div>
            </div>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="font-semibold mb-4">Company</h4>
            <ul className="space-y-2 text-sm text-primary-foreground/80">
              <li>
                <a href="/about" className="hover:text-primary-foreground transition-colors">
                  About Us
                </a>
              </li>
              <li><Link to="/how-it-works" className="hover:text-primary-foreground transition-colors">How It Works</Link></li>
              <li><Link to="/carriers" className="hover:text-primary-foreground transition-colors">Carriers</Link></li>
              <li><Link to="/reviews" className="hover:text-primary-foreground transition-colors">Reviews</Link></li>
              <li><Link to="/contact" className="hover:text-primary-foreground transition-colors">Contact</Link></li>
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="font-semibold mb-4">Legal</h4>
            <ul className="space-y-2 text-sm text-primary-foreground/80">
              <li><Link to="/legal/privacy" className="hover:text-primary-foreground transition-colors">Privacy Policy</Link></li>
              <li><Link to="/legal/terms" className="hover:text-primary-foreground transition-colors">Terms of Service</Link></li>
              <li><Link to="/legal/licenses" className="hover:text-primary-foreground transition-colors">Licenses</Link></li>
              <li><Link to="/legal/disclosures" className="hover:text-primary-foreground transition-colors">Disclosures</Link></li>
              <li><Link to="/legal/accessibility" className="hover:text-primary-foreground transition-colors">Accessibility</Link></li>
            </ul>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-primary-foreground/20 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-xs text-primary-foreground/80">
              © 2024 Safurra. All rights reserved. | Licensed Insurance Agency
            </p>
            <p className="text-xs text-primary-foreground/80 text-center md:text-right max-w-md">
              Safurra is a licensed insurance agency. We partner with top-rated insurance carriers 
              to bring you personalized quotes and coverage options.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;